import React from 'react';
import { TFunction } from 'i18next';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { Task, TaskKindEnum } from 'graphql-common';
import TextCell from '@lib/components/Table/commonComponents/TextCell/TextCell';
import TextLinkCell from '@lib/components/Table/commonComponents/TextLinkCell/TextLinkCell';
import ChipCell from '@lib/components/Table/commonComponents/ChipCell/ChipCell';
import Chip, { ChipSizes, ChipTypes } from '@lib/components/Chip/Chip';
import AssetChip from '@lib/components/AssetChip/AssetChip';
import { colorToChipType } from '@lib/components/Chip/utils/colorEnum';
import doNotRepeatIconSrc from '@lib/assets/icons/custom_do_not_repeat.svg';
import { getPriorityChipLabel, getPriorityChipType } from '@lib/utils/priority';
import { CellProps } from '@lib/components/Table/types';
import { APP_URLS } from 'constants/urls';

type ColumnsArgs = {
  t: TFunction<'translation', undefined>;
};

export default function getDepartmentTasksTableColumns({ t }: ColumnsArgs) {
  return [
    {
      id: 'recurringRule',
      Header: ' ',
      accessor: 'recurringRuleId',
      Cell: (props: CellProps<Task>) => {
        const isOnDemand = _get(props, 'item.kind') === TaskKindEnum.OnDemand;
        const isRepeatable = !_isEmpty(_get(props, 'value'));
        if (isOnDemand) {
          return (
            <Chip
              type={ChipTypes.chipViolet}
              leftIcon="arrow_range"
              leftIconSymbolsOutlined
              tooltip={t('on-demand')}
              asBlockElement
            />
          );
        }
        return (
          <Chip
            type={isRepeatable ? ChipTypes.chipBlue : ChipTypes.chipGray}
            leftIcon={isRepeatable ? 'repeat' : undefined}
            leftImage={isRepeatable ? undefined : doNotRepeatIconSrc}
            tooltip={isRepeatable ? t('recurring') : t('non-recurring')}
            asBlockElement
          />
        );
      },
      width: 44,
    },
    {
      id: 'name',
      Header: t('name'),
      accessor: 'name',
      Cell: (props: CellProps<Task>) => (
        <TextLinkCell
          {...props}
          to={APP_URLS.dashboard.tasks.view.getDynamicPath({
            pathParts: { id: props?.item.id },
          })}
        />
      ),
      sortable: true,
      minWidth: 250,
      maxWidth: 350,
    },
    {
      id: 'formCategoryName',
      Header: t('form-category'),
      Cell: (props: CellProps<Task>) => {
        const label = _get(props, 'item.formTemplate.category.name');
        const type = colorToChipType(
          _get(props, 'item.formTemplate.category.color'),
        );
        if (!label || !type) return '-';
        return <ChipCell label={label} type={type} leftIcon="circle" />;
      },
      sortable: true,
      width: 200,
    },
    {
      id: 'siteName',
      Header: t('site'),
      accessor: 'site',
      Cell: (props: CellProps<Task>) => {
        const label = _get(props, 'item.site.name', '');
        if (!label) return '-';
        return <ChipCell label={label} type={ChipTypes.chipGray} />;
      },
      sortable: true,
      maxWidth: 250,
      minWidth: 130,
    },
    {
      id: 'siteAreaName',
      Header: t('area'),
      accessor: 'siteArea',
      Cell: (props: CellProps<Task>) => {
        const label = _get(props, 'item.siteArea.name', '');
        if (!label) return '-';
        return <ChipCell label={label} type={ChipTypes.chipGray} />;
      },
      sortable: true,
      maxWidth: 250,
      minWidth: 130,
    },
    {
      id: 'assetName',
      Header: t('asset'),
      Cell: ({ item, ...restProps }: CellProps<Task>) =>
        item.asset ? (
          <AssetChip asset={item.asset} size={ChipSizes.small} />
        ) : (
          <TextCell {...restProps} item={item} value={undefined} />
        ),
      sortable: true,
      maxWidth: 250,
      minWidth: 130,
    },
    {
      id: 'priority',
      Header: t('priority'),
      accessor: 'priority',
      Cell: (props: CellProps<Task>) => {
        const priority = _get(props, 'item.priority');
        const label = getPriorityChipLabel(priority);
        if (!label) return '-';
        return (
          <ChipCell
            label={label}
            type={getPriorityChipType(priority)}
            leftIcon="circle"
          />
        );
      },
      sortable: true,
      width: 120,
    },
  ];
}
